import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

const DEFAULT_REASONS = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "hate_speech", label: "Hate Speech" },
  { value: "off_topic", label: "Off-topic" },
  { value: "other", label: "Other" },
];

export default function ReportModal({
  isOpen,
  onClose,
  onSubmit,
  submitting = false,
  comment,
  reasons = DEFAULT_REASONS,
}) {
  const [reason, setReason] = useState(reasons[0]?.value ?? "spam");
  const [description, setDescription] = useState("");

  const commenterName = useMemo(() => {
    if (!comment) return "this comment";
    return comment.author?.username || "this comment";
  }, [comment]);

  useEffect(() => {
    if (isOpen) {
      setReason(reasons[0]?.value ?? "spam");
      setDescription("");
    }
  }, [isOpen, reasons]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const trimmedDescription = description.trim();
    onSubmit({ reason, description: trimmedDescription });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="w-full max-w-md rounded-2xl border border-red-200/50 bg-white p-6 shadow-2xl dark:border-red-500/30 dark:bg-[#161616]">
        <div className="mb-5 space-y-2">
          <h3 className="blog-title text-2xl font-bold text-gray-950 dark:text-white">Report Comment</h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You are reporting {commenterName}. Please select a reason and add any additional details to help moderators review this report.
          </p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="report-reason" className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Reason
            </label>
            <select
              id="report-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-gray-100 dark:focus:border-red-400 dark:focus:ring-red-900/40"
            >
              {reasons.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label htmlFor="report-description" className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Additional details (optional)
            </label>
            <textarea
              id="report-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="Describe what happened..."
              className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-gray-800 dark:bg-[#0E0E0E] dark:text-gray-100 dark:focus:border-red-400 dark:focus:ring-red-900/40"
            />
            <div className="text-right text-xs text-gray-500 dark:text-gray-400">
              {description.length}/1000 characters
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-[#0E0E0E]"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:cursor-not-allowed disabled:bg-red-400"
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

ReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  comment: PropTypes.shape({
    author: PropTypes.shape({
      username: PropTypes.string,
    }),
  }),
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};
